/**
 * Every phone number goes through here: stored and sent as E.164
 * ("+15551234567"), shown as "(555) 123-4567".
 *
 * US only: every number we text or call from is +1. A number that is not a
 * whole US number is passed through as given, never mangled into one.
 */

/** A US number without its country code. */
const US_DIGITS = 10,

  /** E.164 allows 15 digits; the shortest real national numbers run to 8. */
  INTERNATIONAL = /^\+[2-9]\d{7,14}$/u,

  /** "5551234567" from "+1 (555) 123-4567", or null when it is not a whole US number. */
  usNumber = (input: string): string | null => {
    const digits = input.replace(/\D/g, ""),
      local = digits.length === US_DIGITS + 1 && digits.startsWith("1") ? digits.slice(1) : digits;
    // No US area code starts with 0 or 1.
    if (local.length === US_DIGITS && /^[2-9]/u.test(local)) {
      return local;
    }
    return null;
  },

  /** "+15551234567" from any shape of a US number; an E.164 number from elsewhere passes. Null otherwise. */
  toE164 = (input: string | null | undefined): string | null => {
    if (input === undefined || input === null || input === "") {
      return null;
    }
    const trimmed = input.trim(),
      compact = trimmed.replace(/[\s().-]/g, "");
    if (INTERNATIONAL.test(compact)) {
      return compact;
    }
    const local = usNumber(trimmed);
    if (local) {
      return `+1${local}`;
    }
    return null;
  },

  /** "(555) 123-4567" from any shape of a US number. Anything else comes back as given. */
  formatPhone = (input: string | null | undefined): string => {
    if (input === undefined || input === null || input === "") {
      return "";
    }
    const local = usNumber(input);
    if (!local) {
      return input;
    }
    return `(${local.slice(0, 3)}) ${local.slice(3, 6)}-${local.slice(6)}`;
  },

  /** "tel:+15551234567", for a link that dials. */
  phoneHref = (input: string | null | undefined): string => `tel:${toE164(input) ?? (input ?? "").replace(/[^\d+]/g, "")}`,

  /** Whether two strings are the same number, whatever the punctuation or country code. */
  samePhone = (a: string | null | undefined, b: string | null | undefined): boolean => {
    const left = toE164(a);
    return left !== null && left === toE164(b);
  },

  // ── As a person types ──────────────────────────────────────────────────────

  /**
   * The US number in whatever was typed or pasted so far, at most ten digits.
   * A leading 1 is always the country code, so "+1 555", "1555" and "555" all
   * read as "555".
   */
  phoneDigits = (input: string): string => input.replace(/\D/g, "").replace(/^1/u, "").slice(0, US_DIGITS),

  /** `(555) 123-4567`, partial input included: `5551` is `(555) 1`. */
  formatPhoneAsUserTypes = (input: string): string => {
    const local = phoneDigits(input);
    if (local.length === 0) {
      return "";
    }
    if (local.length <= 3) {
      return `(${local}`;
    }
    if (local.length <= 6) {
      return `(${local.slice(0, 3)}) ${local.slice(3)}`;
    }
    return `(${local.slice(0, 3)}) ${local.slice(3, 6)}-${local.slice(6)}`;
  },

  /**
   * The field's next value, given what the input now holds and what it held.
   *
   * Formatting alone would trap a backspace: deleting the ")" of "(555) 1"
   * leaves the same digits, which format straight back to "(555) 1". A deletion
   * that removed only punctuation takes the digit in front of it instead.
   */
  nextPhoneInput = (next: string, previous: string): string => {
    const before = phoneDigits(previous);
    if (next.length < previous.length && phoneDigits(next) === before && before.length > 0) {
      let at = 0;
      while (at < next.length && next[at] === previous[at]) {
        at += 1;
      }
      const digitsInFront = phoneDigits(previous.slice(0, at)).length;
      if (digitsInFront > 0) {
        return formatPhoneAsUserTypes(
          before.slice(0, digitsInFront - 1) + before.slice(digitsInFront),
        );
      }
    }
    return formatPhoneAsUserTypes(next);
  },

  /**
   * Empty input reports `error: null`: a field nobody has typed in yet should
   * not be shouting.
   */
  validatePhoneInput = (input: string): {
    valid: boolean;
    e164: string | null;
    error: string | null;
  } => {
    if (input.trim().length === 0) {
      return { e164: null, error: null, valid: false };
    }
    const local = phoneDigits(input);
    if (local.length === 0) {
      return { e164: null, error: "Enter a 10-digit US phone number.", valid: false };
    }
    if (local.length < US_DIGITS) {
      const remaining = US_DIGITS - local.length;
      return {
        e164: null,
        error: `${remaining} more digit${remaining === 1 ? "" : "s"} to go.`,
        valid: false,
      };
    }
    if (local.startsWith("0")) {
      return { e164: null, error: "US area codes can't start with 0.", valid: false };
    }
    return { e164: `+1${local}`, error: null, valid: true };
  };

export {
  toE164,
  formatPhone,
  phoneHref,
  samePhone,
  phoneDigits,
  formatPhoneAsUserTypes,
  nextPhoneInput,
  validatePhoneInput,
};
